/**
 * Action Guidance Library
 * 
 * Provides detailed guidance for case actions without modifying database schema.
 * Organized by case type for context-specific guidance.
 */

// Case-level guidance (for Overview tab)
export const caseTypeGuidance = {
  disciplinary: {
    title: "Disciplinary Process Guidance",
    importance: `Disciplinary procedures are legally sensitive and must follow ACAS Code of Practice. Failure to follow fair procedures can result in unfair dismissal claims, which can be costly (basic award + compensatory award, plus potential 25% uplift for ACAS Code breach).`,
    whatToLookFor: [
      "**Consistency:** Treat similar cases similarly to avoid discrimination claims",
      "**Evidence:** Ensure all allegations are supported by solid evidence",
      "**Procedural fairness:** Follow each step - shortcuts undermine your case",
      "**Time limits:** Act promptly - delays suggest issue isn't serious",
      "**Documentation:** Keep detailed records of every step and decision",
      "**Independence:** Ensure decision-makers weren't involved in investigation"
    ],
    bestPractices: [
      "Always try informal resolution first before formal disciplinary",
      "Give employee reasonable notice (minimum 48 hours for hearings)",
      "Allow employee to be accompanied at all formal meetings",
      "Provide all evidence in advance - no surprises at hearing",
      "Consider mitigating circumstances and employee's record",
      "Explain right of appeal and make appeal process genuinely independent",
      "Keep detailed notes of all meetings and decisions",
      "Consider alternatives to dismissal (warnings, training, support)"
    ],
    commonPitfalls: [
      "Rushing to formal action without trying informal resolution",
      "Insufficient investigation before reaching conclusions",
      "Failing to give adequate notice or provide evidence",
      "Predetermined outcomes (decision made before hearing)",
      "Inconsistent treatment of similar cases",
      "No genuine right of appeal",
      "Poor documentation of process and decisions"
    ],
    keyLegalPoints: [
      "Must have fair reason for dismissal (capability, conduct, redundancy, statutory bar, some other substantial reason)",
      "Must follow fair procedure (ACAS Code applies)",
      "Employee needs 2 years service for ordinary unfair dismissal (but not for automatic unfair dismissal)",
      "Gross misconduct can justify summary dismissal (no notice)",
      "Warnings typically expire after set period (6-12 months)"
    ]
  },

  grievance: {
    title: "Grievance Process Guidance",
    importance: `Grievances are employee complaints about work, working conditions, or relationships. Unresolved grievances can lead to resignations, constructive dismissal claims, and toxic workplace culture. ACAS Code requires employers to handle grievances promptly and fairly.`,
    whatToLookFor: [
      "**Underlying issues:** What's really bothering the employee?",
      "**Patterns:** Is this part of wider team/management issues?",
      "**Urgency:** Does this need immediate action (harassment, safety)?",
      "**Relationships:** Can working relationships be repaired?",
      "**Legal risk:** Could this lead to discrimination/whistleblowing claims?",
      "**Resolution opportunities:** Can this be resolved informally?"
    ],
    bestPractices: [
      "Respond quickly - acknowledge within 5 working days",
      "Try informal resolution first (ACAS priority)",
      "Listen without being defensive - employee needs to feel heard",
      "Appoint impartial investigator (not the person complained about)",
      "Keep employee updated on progress and timescales",
      "Focus on resolving the issue, not winning an argument",
      "Consider mediation for relationship breakdowns",
      "Document everything, even informal discussions"
    ],
    commonPitfalls: [
      "Dismissing grievance as trivial or oversensitive",
      "Investigating yourself when you're implicated",
      "Taking sides before investigation complete",
      "Retaliating against employee for raising grievance",
      "Ignoring or delaying response",
      "Failing to follow up on agreed actions",
      "Not offering right of appeal"
    ],
    keyLegalPoints: [
      "ACAS Code applies to grievances as well as disciplinary",
      "Constructive dismissal if employer's breach forces resignation",
      "Protected disclosures (whistleblowing) have special protections",
      "Discrimination grievances must be taken very seriously",
      "Employee can resign and claim if grievance badly handled",
      "Compensation can be increased 25% for ACAS Code breach"
    ]
  },

  "performance-capability": {
    title: "Performance & Capability Management Guidance",
    importance: `Performance management is about helping employees succeed, not catching them out. Fair capability processes require clear standards, adequate support, and reasonable time to improve. Dismissals for capability must show the employer acted reasonably.`,
    whatToLookFor: [
      "**Root causes:** Is it capability (can't do) or conduct (won't do)?",
      "**Support needs:** What training, coaching, or resources are needed?",
      "**Reasonable adjustments:** Does Equality Act apply (disability)?",
      "**Timescales:** Is improvement timeframe realistic?",
      "**Measurement:** Are performance standards clear and measurable?",
      "**External factors:** Are there business changes affecting performance?"
    ],
    bestPractices: [
      "Set SMART objectives - specific, measurable, achievable, relevant, time-bound",
      "Provide adequate support, training, and resources",
      "Regular feedback and coaching (don't wait for formal reviews)",
      "Document performance issues contemporaneously",
      "Allow reasonable time for improvement (typically 4-12 weeks)",
      "Consider if role is right fit - explore alternatives",
      "Celebrate improvements, however small",
      "Be consistent across team members"
    ],
    commonPitfalls: [
      "Vague performance standards ('work harder', 'be better')",
      "Unrealistic improvement timescales",
      "Failing to provide promised support or training",
      "Moving goalposts once targets are set",
      "Not considering reasonable adjustments for disabilities",
      "Treating capability issue as disciplinary matter",
      "Insufficient documentation of performance concerns"
    ],
    keyLegalPoints: [
      "Capability is a fair reason for dismissal if handled properly",
      "Must show employee was warned and given chance to improve",
      "Reasonable adjustments required for disabled employees",
      "Consider redeployment before dismissal",
      "Ill-health capability requires medical evidence",
      "Performance standards must be reasonable and achievable"
    ]
  },

  "absence-management": {
    title: "Absence Management Guidance",
    importance: `Managing absence requires balancing business needs with employee wellbeing and legal obligations. Disability discrimination claims are common in absence cases. Medical evidence and reasonable adjustments are crucial.`,
    whatToLookFor: [
      "**Disability:** Does Equality Act 2010 apply?",
      "**Patterns:** Is absence related to work issues (stress, bullying)?",
      "**Medical evidence:** What do doctors/OH say about prognosis?",
      "**Adjustments:** What could help employee return to work?",
      "**Impact:** How is absence affecting business operations?",
      "**Alternatives:** Can role be adjusted or redeployment considered?"
    ],
    bestPractices: [
      "Maintain regular, supportive contact during absence",
      "Obtain Occupational Health advice early",
      "Consider phased return to work",
      "Explore reasonable adjustments (flexible hours, homeworking, modified duties)",
      "Keep detailed absence records",
      "Be patient - recovery takes time",
      "Focus on 'when can you return?' not 'why aren't you back?'",
      "Consider ill-health retirement if appropriate"
    ],
    commonPitfalls: [
      "Treating absence as disciplinary matter",
      "Failing to get medical evidence",
      "Not considering reasonable adjustments",
      "Pressuring employee to return before ready",
      "Dismissing without exploring alternatives",
      "Inconsistent treatment of different absence types",
      "Poor communication during absence"
    ],
    keyLegalPoints: [
      "Disability discrimination if failure to make reasonable adjustments",
      "Must have medical evidence before capability dismissal",
      "Can't dismiss for disability-related absence without justification",
      "Reasonable adjustments duty is ongoing, not one-off",
      "Stress-related absence may indicate workplace issues",
      "Pregnancy-related absence has special protections"
    ]
  },

  "exit-dismissal": {
    title: "Exit & Dismissal Process Guidance",
    importance: `Dismissal is the most serious employment sanction and carries highest legal risk. Unfair dismissal claims are common and can be expensive. Every dismissal must have a fair reason and follow fair procedure.`,
    whatToLookFor: [
      "**Fair reason:** Do you have one of the five fair reasons?",
      "**Procedure:** Have you followed fair process throughout?",
      "**Alternatives:** Have you genuinely considered alternatives to dismissal?",
      "**Consistency:** How have similar cases been handled?",
      "**Evidence:** Is evidence solid and documented?",
      "**Reasonableness:** Would other reasonable employers dismiss?"
    ],
    bestPractices: [
      "Ensure thorough investigation before dismissal decision",
      "Give clear warning that dismissal is possible outcome",
      "Provide all evidence well in advance of hearing",
      "Use independent decision-maker (not investigator)",
      "Consider employee's length of service and record",
      "Explore alternatives (warnings, demotion, redeployment)",
      "Offer genuine right of appeal",
      "Get legal advice before dismissing"
    ],
    commonPitfalls: [
      "Predetermined decision before hearing",
      "Insufficient investigation or evidence",
      "Failing to consider alternatives to dismissal",
      "Inconsistent treatment compared to similar cases",
      "No genuine right of appeal",
      "Wrong notice period or final pay calculations",
      "Poor communication of decision and reasons"
    ],
    keyLegalPoints: [
      "Five fair reasons: capability, conduct, redundancy, statutory bar, SOSR",
      "Must follow fair procedure (ACAS Code for conduct/capability)",
      "Summary dismissal only for gross misconduct",
      "Must give statutory minimum notice (or payment in lieu)",
      "Basic award: 0.5-1.5 weeks pay per year (max £571/week, max £17,130)",
      "Compensatory award: actual losses (max £115,115 or year's pay)",
      "25% uplift possible for ACAS Code breach"
    ]
  },

  "tribunal-prep": {
    title: "Employment Tribunal Preparation Guidance",
    importance: `Employment tribunals are formal legal proceedings. Defending claims is expensive (legal fees £10k-50k+) and time-consuming. Early settlement often makes commercial sense. Proper preparation is essential if defending.`,
    whatToLookFor: [
      "**Merits:** Honest assessment of strengths and weaknesses",
      "**Evidence:** What documents and witnesses do you have?",
      "**Costs:** Legal fees, management time, compensation risk",
      "**Reputation:** Public hearing - media interest?",
      "**Settlement value:** What would reasonable settlement be?",
      "**Precedent:** Will outcome affect other cases/employees?"
    ],
    bestPractices: [
      "Get specialist employment law advice immediately",
      "Conduct honest merits assessment - don't be over-optimistic",
      "Preserve all relevant documents (litigation hold)",
      "Consider ACAS Early Conciliation seriously",
      "Calculate worst-case compensation scenario",
      "Prepare witnesses thoroughly",
      "Keep board/senior management informed",
      "Consider settlement at every stage"
    ],
    commonPitfalls: [
      "Underestimating risk and costs of defending",
      "Missing critical deadlines (28 days for ET3)",
      "Destroying or losing key documents",
      "Refusing reasonable settlement offers",
      "Poor witness preparation",
      "Inconsistent evidence between witnesses",
      "Not taking legal advice early enough"
    ],
    keyLegalPoints: [
      "Strict time limits: 3 months less 1 day for most claims",
      "ACAS Early Conciliation is mandatory before claim",
      "28 days to file ET3 response (extensions rare)",
      "Disclosure obligations - must provide relevant documents",
      "Witness statements must be exchanged in advance",
      "Tribunal decisions are public and published online",
      "Can appeal to EAT on point of law only"
    ]
  },

  settlement: {
    title: "Settlement Agreement Guidance",
    importance: `Settlement agreements allow parties to resolve disputes confidentially without tribunal. They must meet strict legal requirements to be binding. Properly drafted settlements provide certainty and closure for both parties.`,
    whatToLookFor: [
      "**Commercial value:** What's the cost of defending vs settling?",
      "**Tax treatment:** Structure payment to minimize tax",
      "**Waiver scope:** What claims are being waived?",
      "**Confidentiality:** How important is confidentiality?",
      "**Reference:** What reference will be provided?",
      "**Timing:** When does employee leave and get paid?"
    ],
    bestPractices: [
      "Be realistic about settlement value based on risk",
      "Structure payment tax-efficiently (first £30k tax-free)",
      "Include comprehensive waiver of all claims",
      "Agree reference terms in writing",
      "Include confidentiality and non-disparagement clauses",
      "Contribute to employee's legal fees (typically £500-£1,500)",
      "Set clear payment deadline",
      "Get employee to sign quickly before they change mind"
    ],
    commonPitfalls: [
      "Offering too little - employee rejects and claims anyway",
      "Poor tax advice - unnecessary tax liability",
      "Inadequate waiver - employee can still claim",
      "No confidentiality clause - employee talks publicly",
      "Vague reference terms - disputes later",
      "Delayed payment - damages relationship",
      "Not getting independent legal advice certificate"
    ],
    keyLegalPoints: [
      "Must be in writing and relate to specific complaint/proceedings",
      "Employee must receive independent legal advice",
      "Adviser must have professional indemnity insurance",
      "First £30,000 of compensation is tax-free",
      "Notice pay and holiday pay are taxable",
      "Cannot waive personal injury claims not yet known",
      "COT3 agreements (via ACAS) are also binding"
    ]
  }
};

export const actionGuidance = {
  // DISCIPLINARY CASE TYPE
  disciplinary: {
    "Informal discussion with employee about concerns": {
      guidance: `**Purpose:** Address performance or conduct issues early before they escalate.

**Preparation:**
• Review specific examples of concerning behavior
• Check employee's personnel file
• Choose private, neutral location
• Schedule 30-60 minutes

**During Meeting:**
• Explain specific concerns with examples
• Ask for employee's perspective
• Explore underlying causes
• Discuss improvement expectations
• Agree on support and review date

**ACAS Best Practice:** This is informal - goal is to resolve without formal action.`,
      tips: [
        "Focus on behaviors, not personality",
        "Use 'I noticed...' rather than 'You always...'",
        "Document immediately after"
      ],
      warnings: [
        "This is NOT a disciplinary hearing",
        "Don't make threats about formal action"
      ]
    },

    "Document concerns and discussion outcomes": {
      guidance: `**Legal Requirement:** Contemporaneous records are crucial.

**What to Document:**
• Date, time, location, attendees
• Specific concerns with examples
• Employee's response
• Support offered
• Expectations set and timescales

**How to Document:**
• Write factually and objectively
• Use direct quotes where possible
• Write as if it will be read in tribunal`,
      tips: [
        "Complete within 24 hours",
        "Have employee sign to confirm discussion",
        "Upload to case documents"
      ],
      warnings: [
        "May be disclosed in tribunal",
        "Inaccurate records undermine your case"
      ]
    },

    "Send hearing invitation (>=48h notice)": {
      guidance: `**Legal Requirement:** ACAS Code requires minimum 48 hours notice.

**Invitation Must Include:**
• Date, time, location
• Nature of allegations
• Potential consequences
• Right to be accompanied
• Copies of evidence

**Timing:** Minimum 48 hours, best practice 5-7 days`,
      tips: [
        "Send by recorded delivery and email",
        "Attach all evidence",
        "Confirm receipt"
      ],
      warnings: [
        "Insufficient notice can make dismissal unfair",
        "Must allow companion"
      ]
    },

    "Set clear expectations and improvement targets": {
      guidance: `**Purpose:** Employee must know exactly what's expected to improve.

**Setting Expectations:**
• Be specific and measurable
• Explain why standards matter
• Ensure expectations are achievable
• Put in writing

**Examples:**
• "Arrive by 9am daily" not "Be on time"
• "Complete 5 reports per week" not "Work harder"

**Follow-up:**
• Schedule regular check-ins
• Provide ongoing feedback
• Adjust if circumstances change`,
      tips: [
        "Involve employee in setting targets",
        "Link expectations to job description",
        "Celebrate improvements"
      ],
      warnings: [
        "Vague expectations are unfair",
        "Don't move goalposts once set"
      ]
    },

    "Offer support, training, or reasonable adjustments": {
      guidance: `**Purpose:** Give employee every chance to succeed.

**Types of Support:**
• Training courses or coaching
• Mentoring from experienced colleague
• Additional supervision
• Reasonable adjustments (if disability)
• Workload adjustments
• Equipment or resources

**Documentation:**
• Record what support was offered
• Note if employee declined support
• Track whether support was provided as promised`,
      tips: [
        "Ask employee what support they need",
        "Be genuine - don't just tick boxes",
        "Follow through on promises"
      ],
      warnings: [
        "Failing to provide promised support undermines case",
        "Consider Equality Act obligations"
      ]
    },

    "Consider suspension (with pay) if necessary": {
      guidance: `**When to Suspend:**
• Serious allegations (gross misconduct)
• Risk to people, property, or business
• Potential evidence tampering
• Breakdown of working relationships

**Must Be:**
• With full pay (not a punishment)
• For as short a time as possible
• Kept under review
• Neutral act (not presumption of guilt)

**Process:**
• Explain reasons for suspension
• Confirm in writing
• Set review dates
• Maintain contact`,
      tips: [
        "Only suspend if genuinely necessary",
        "Don't suspend as knee-jerk reaction",
        "Consider alternatives (different location, duties)"
      ],
      warnings: [
        "Suspension can damage employee wellbeing",
        "Lengthy suspension can be breach of trust",
        "Must be neutral, not punitive"
      ]
    },

    "Appoint investigating officer (independent)": {
      guidance: `**Independence is Critical:**
• Not the complainant or witness
• Not line manager if they're involved
• No predetermined views
• Sufficient seniority and experience

**Role of Investigator:**
• Gather facts impartially
• Interview all relevant parties
• Collect documentary evidence
• Prepare factual report
• Make findings, not recommendations on sanction

**Brief Investigator:**
• Terms of reference
• Timeline expectations
• Confidentiality requirements`,
      tips: [
        "Choose someone trained in investigations",
        "Provide investigation checklist",
        "Set realistic timescales"
      ],
      warnings: [
        "Biased investigator undermines entire process",
        "Investigator shouldn't be decision-maker at hearing"
      ]
    },

    "Gather evidence: statements, documents, CCTV": {
      guidance: `**Types of Evidence:**
• Witness statements (written and signed)
• Emails, messages, documents
• CCTV footage (preserve immediately)
• Attendance records, timesheets
• Previous warnings or records
• Policies and procedures

**Evidence Handling:**
• Preserve original evidence
• Maintain chain of custody
• Redact third-party personal data
• Number and date all documents
• Store securely

**GDPR Compliance:**
• Only collect relevant evidence
• Don't retain longer than necessary
• Protect confidentiality`,
      tips: [
        "Act quickly - CCTV may be overwritten",
        "Get written statements, not verbal accounts",
        "Keep evidence log"
      ],
      warnings: [
        "Missing evidence weakens case",
        "Tampering with evidence is serious misconduct",
        "Respect data protection laws"
      ]
    },

    "Interview relevant parties and witnesses": {
      guidance: `**Preparation:**
• Prepare questions in advance
• Book private room
• Allow adequate time
• Explain purpose of interview

**During Interview:**
• Take detailed notes or record (with consent)
• Ask open questions
• Don't lead or suggest answers
• Allow interviewee to add anything
• Summarize key points

**After Interview:**
• Write up notes promptly
• Ask interviewee to review and sign
• Keep confidential`,
      tips: [
        "Interview witnesses separately",
        "Stay neutral - don't show bias",
        "Ask 'what, when, where, who' not 'why'"
      ],
      warnings: [
        "Leading questions undermine evidence",
        "Don't promise confidentiality you can't keep",
        "Respect if witness declines to participate"
      ]
    },

    "Prepare investigation report with findings": {
      guidance: `**Report Must Include:**
• Summary of allegations
• Investigation process followed
• Evidence gathered
• Witness accounts
• Findings of fact
• Whether allegations substantiated

**Writing Style:**
• Factual and objective
• Clear and structured
• Avoid opinions or judgments
• Separate facts from conclusions

**Findings:**
• On balance of probabilities
• Based on evidence, not speculation
• Explain reasoning`,
      tips: [
        "Use investigation report template",
        "Have someone review before finalizing",
        "Attach all evidence as appendices"
      ],
      warnings: [
        "Report will be disclosed to employee",
        "Don't recommend specific sanction",
        "Avoid inflammatory language"
      ]
    },

    "Include allegations, evidence, right to be accompanied": {
      guidance: `**Allegations:**
• State clearly what rules were breached
• Provide specific dates and details
• Explain why it's a disciplinary matter
• Reference relevant policies

**Evidence:**
• Attach investigation report
• Include all documents to be relied on
• Provide witness statements
• Don't withhold evidence

**Right to be Accompanied:**
• Statutory right to work colleague or union rep
• Explain companion's role
• Allow reasonable time to arrange`,
      tips: [
        "Be specific - vague allegations are unfair",
        "Number and paginate evidence",
        "Offer to discuss before hearing"
      ],
      warnings: [
        "Ambushing with new evidence is unfair",
        "Denying companion is legal breach",
        "Inadequate detail prejudices defense"
      ]
    },

    "Hold hearing with independent chair": {
      guidance: `**Chair Must Be:**
• Independent (not involved in investigation)
• Sufficient seniority to make decision
• Trained in disciplinary procedures
• Impartial and open-minded

**Hearing Structure:**
• Introductions and explain process
• Present allegations and evidence
• Employee responds and presents case
• Questions and clarifications
• Adjourn to consider decision

**During Hearing:**
• Take detailed notes
• Allow employee to speak
• Consider all evidence fairly
• Don't rush to judgment`,
      tips: [
        "Follow hearing script/checklist",
        "Allow breaks if needed",
        "Stay calm and professional"
      ],
      warnings: [
        "Predetermined decision is unfair",
        "Chair must be genuinely independent",
        "Procedural errors can make dismissal unfair"
      ]
    },

    "Allow employee to present case and call witnesses": {
      guidance: `**Employee's Right:**
• Present their version of events
• Call relevant witnesses
• Provide evidence and documents
• Challenge allegations
• Explain mitigating circumstances

**Facilitating Defense:**
• Give adequate time to present
• Listen without interrupting
• Ask clarifying questions
• Consider evidence fairly
• Allow companion to contribute

**Witnesses:**
• Employee can request witnesses
• Must be relevant to case
• Can question witnesses
• Take notes of testimony`,
      tips: [
        "Don't dismiss employee's evidence out of hand",
        "Consider mitigating factors seriously",
        "Allow emotional employee time to compose"
      ],
      warnings: [
        "Preventing employee from presenting case is unfair",
        "Must genuinely consider their evidence",
        "Rushing hearing prejudices defense"
      ]
    },

    "Adjourn to consider decision and record outcome": {
      guidance: `**Adjournment:**
• Don't make decision in heat of moment
• Take time to consider all evidence
• Review investigation report
• Consider precedents and consistency
• Weigh mitigating factors

**Decision-Making:**
• Is misconduct proven on balance of probabilities?
• What is appropriate sanction?
• Have we considered alternatives?
• Is this consistent with previous cases?
• Is dismissal reasonable response?

**Recording:**
• Document reasoning
• Note factors considered
• Record decision clearly`,
      tips: [
        "Take adequate time - don't rush",
        "Consult HR or legal if unsure",
        "Consider range of sanctions"
      ],
      warnings: [
        "Hasty decisions are often wrong",
        "Must have clear reasoning",
        "Inconsistency undermines fairness"
      ]
    },

    "Determine appropriate sanction": {
      guidance: `**Sanction Options:**
• No action (if not proven)
• Verbal warning (informal)
• First written warning (6-12 months)
• Final written warning (12 months)
• Dismissal with notice
• Summary dismissal (gross misconduct)

**Factors to Consider:**
• Severity of misconduct
• Employee's disciplinary record
• Length of service
• Mitigating circumstances
• Consistency with previous cases
• Impact on business

**Proportionality:**
• Sanction must fit the offense
• Consider if lesser sanction appropriate
• Dismissal is last resort`,
      tips: [
        "Check previous similar cases",
        "Consider employee's record",
        "Explain reasoning for sanction chosen"
      ],
      warnings: [
        "Disproportionate sanction is unfair",
        "Inconsistency can be discrimination",
        "Must consider alternatives to dismissal"
      ]
    },

    "Issue written outcome letter with reasons": {
      guidance: `**Letter Must Include:**
• Clear statement of decision
• Findings of fact
• Reasons for decision
• Sanction imposed and duration
• Consequences of further misconduct
• Right of appeal and deadline
• Appeal process

**Reasons:**
• Explain why sanction chosen
• Reference evidence relied upon
• Show you considered mitigating factors
• Explain why lesser sanction not appropriate

**Timing:**
• Send within 5 working days of hearing
• Don't delay - creates uncertainty`,
      tips: [
        "Use outcome letter template",
        "Have HR review before sending",
        "Send by recorded delivery and email"
      ],
      warnings: [
        "Inadequate reasons can make dismissal unfair",
        "Letter may be used in tribunal",
        "Must explain right of appeal clearly"
      ]
    },

    "Explain right of appeal (5 working days)": {
      guidance: `**Appeal Rights:**
• Employee has right to appeal
• Usually 5 working days to appeal
• Must explain how to appeal
• Appeal should be to more senior manager

**Grounds for Appeal:**
• Procedural unfairness
• Decision was wrong
• Sanction too harsh
• New evidence available

**Appeal Process:**
• Acknowledge appeal
• Arrange appeal hearing
• Genuinely reconsider decision
• Can uphold, reduce, or overturn`,
      tips: [
        "Make appeal process clear and accessible",
        "Use different person for appeal",
        "Take appeals seriously"
      ],
      warnings: [
        "No genuine appeal makes dismissal unfair",
        "Appeal can't just rubber-stamp decision",
        "Must consider new evidence"
      ]
    },

    "Set up monitoring arrangements if warning issued": {
      guidance: `**Monitoring Plan:**
• Clear expectations going forward
• Regular review meetings (weekly/monthly)
• Who will monitor performance
• How long monitoring will last
• Support to be provided

**Documentation:**
• Record monitoring arrangements
• Keep notes of review meetings
• Document improvements or concerns
• File contemporaneous records

**Review:**
• Provide ongoing feedback
• Adjust support if needed
• Confirm when warning expires`,
      tips: [
        "Schedule review meetings in advance",
        "Be supportive, not punitive",
        "Celebrate improvements"
      ],
      warnings: [
        "Monitoring should help, not harass",
        "Don't extend warning period unfairly",
        "Keep monitoring proportionate"
      ]
    }
  },

  // GRIEVANCE CASE TYPE
  grievance: {
    "Attempt informal resolution through discussion": {
      guidance: `**ACAS Code:** Always try to resolve informally first.

**Approach:**
• Create safe space for dialogue
• Listen without interrupting
• Explore solutions together
• Be willing to compromise

**When to Escalate:**
• Cannot resolve informally
• Employee requests formal process
• Serious allegations`,
      tips: [
        "Most grievances resolve at this stage",
        "Focus on mutually acceptable solutions",
        "Document attempts"
      ],
      warnings: [
        "Don't pressure to drop grievance",
        "Respect request for formal process"
      ]
    },

    "Acknowledge receipt within 5 working days": {
      guidance: `**ACAS Best Practice:** Prompt acknowledgment shows seriousness.

**Must Include:**
• Confirmation of receipt
• Who will investigate
• Indicative timescale
• Right to be accompanied`,
      tips: [
        "Send within 5 days",
        "Appoint impartial investigator"
      ],
      warnings: [
        "Delays worsen situation",
        "Don't investigate if you're implicated"
      ]
    },

    "Employee raises concern with line manager": {
      guidance: `**Initial Response:**
• Listen without interrupting
• Take concern seriously
• Thank employee for raising it
• Ask clarifying questions
• Avoid immediate judgments

**Assess Severity:**
• Is it a query or genuine grievance?
• Does it need immediate action?
• Can it be resolved informally?
• Are there safety/legal issues?

**Next Steps:**
• Explain you'll look into it
• Agree timescale for response
• Suggest informal resolution if appropriate
• Explain formal process if needed`,
      tips: [
        "Stay calm even if criticism is personal",
        "Take notes during conversation",
        "Act quickly - delays make issues worse"
      ],
      warnings: [
        "Don't dismiss as trivial",
        "Don't retaliate against employee",
        "Some issues must be formal (harassment, discrimination)"
      ]
    },

    "Document attempted resolution": {
      guidance: `**Why Document:**
• Evidence you tried informal resolution
• Shows good faith effort
• Protects if escalates to formal
• Demonstrates ACAS Code compliance

**What to Record:**
• Date and details of discussions
• Employee's concerns
• Solutions explored or offered
• Employee's response
• Outcome or reasons why didn't work

**Style:**
• Factual and balanced
• Include both perspectives
• Note any agreements
• Keep confidential`,
      tips: [
        "Document even if resolution fails",
        "Note partial improvements",
        "Record employee's preferred next steps"
      ],
      warnings: [
        "Don't blame employee for not resolving",
        "Avoid defensive language",
        "Don't prejudge formal grievance"
      ]
    },

    "If unresolved, advise formal grievance process": {
      guidance: `**Explain to Employee:**
• How to submit formal grievance
• What to include in written grievance
• Timescales for response
• Right to be accompanied
• What happens next

**Provide:**
• Grievance policy document
• Grievance form (if used)
• Contact details for HR
• Union/representative information

**Tone:**
• Supportive, not defensive
• Explain it's their right
• Don't discourage from proceeding`,
      tips: [
        "Give employee time to consider",
        "Offer to answer questions about process",
        "Don't pressure to drop grievance"
      ],
      warnings: [
        "Discouraging formal grievance is unfair",
        "Employee has right to formal process",
        "Don't make it sound threatening"
      ]
    },

    "Employee submits written grievance": {
      guidance: `**Receipt:**
• Acknowledge receipt promptly
• Confirm you've read it
• Explain next steps
• Set expectations on timescales

**Review Grievance:**
• What is employee complaining about?
• Is it clear and specific?
• Are there multiple issues?
• Who is implicated?
• How serious is it?

**Initial Actions:**
• Preserve any evidence
• Consider if interim measures needed
• Appoint investigator
• Plan investigation approach`,
      tips: [
        "Treat all grievances seriously",
        "Act quickly on serious allegations",
        "Keep employee informed"
      ],
      warnings: [
        "Don't ignore or delay",
        "Don't contact people implicated before planning",
        "Respect confidentiality"
      ]
    },

    "Appoint investigating manager (independent)": {
      guidance: `**Independence:**
• Not involved in the grievance
• Not close friend of parties
• No predetermined views
• Sufficient seniority
• Trained in investigations

**Investigator's Role:**
• Gather facts impartially
• Interview all parties
• Collect evidence
• Prepare report
• Make findings

**Brief Investigator:**
• Terms of reference
• Timeline
• Confidentiality
• Support available`,
      tips: [
        "Choose someone with investigation training",
        "Provide investigation toolkit",
        "Set realistic timescales"
      ],
      warnings: [
        "Biased investigator undermines process",
        "Don't investigate if you're named in grievance",
        "Investigator shouldn't decide outcome"
      ]
    },

    "Invite to grievance hearing (48 hours notice)": {
      guidance: `**Invitation Must Include:**
• Date, time, location
• Purpose of hearing
• Who will attend
• Right to be accompanied
• Opportunity to bring evidence
• Copy of grievance policy

**Timing:**
• Minimum 48 hours notice
• Best practice: 5-7 days
• Allow time to prepare
• Consider employee's availability

**Accessibility:**
• Offer adjustments if needed
• Provide documents in advance
• Explain hearing format`,
      tips: [
        "Send by email and post",
        "Confirm receipt",
        "Be flexible with dates if reasonable"
      ],
      warnings: [
        "Insufficient notice is unfair",
        "Must allow companion",
        "Don't rush hearing"
      ]
    },

    "Confirm right to be accompanied": {
      guidance: `**Statutory Right:**
• Work colleague OR trade union rep
• Can address hearing
• Can confer with employee
• Cannot answer questions for employee

**Process:**
• Ask who employee wants
• Allow time to arrange
• Confirm companion's availability
• Explain companion's role

**If No Companion:**
• Employee can proceed alone
• Confirm in writing they've waived right
• Offer to reschedule if they change mind`,
      tips: [
        "Be flexible with dates for companion",
        "Brief companion on their role",
        "Don't refuse reasonable choice"
      ],
      warnings: [
        "Denying companion is legal breach",
        "Can't refuse because companion is 'difficult'",
        "Companion can't be penalized for attending"
      ]
    },

    "Investigate grievance thoroughly": {
      guidance: `**Investigation Steps:**
• Review grievance and evidence
• Identify who to interview
• Gather relevant documents
• Check policies and procedures
• Look for patterns or history

**Scope:**
• Focus on grievance raised
• Don't expand unnecessarily
• But don't ignore related issues
• Consider wider context

**Impartiality:**
• Keep open mind
• Don't prejudge
• Consider all perspectives
• Base findings on evidence`,
      tips: [
        "Create investigation plan",
        "Keep detailed records",
        "Act promptly but thoroughly"
      ],
      warnings: [
        "Superficial investigation is unfair",
        "Don't ignore inconvenient evidence",
        "Respect confidentiality"
      ]
    },

    "Interview relevant parties and witnesses": {
      guidance: `**Preparation:**
• Prepare questions
• Book private room
• Explain purpose
• Allow adequate time

**During Interview:**
• Take detailed notes
• Ask open questions
• Don't lead witnesses
• Allow them to add anything
• Summarize key points

**After Interview:**
• Write up promptly
• Ask interviewee to review
• Keep confidential
• Store securely`,
      tips: [
        "Interview separately",
        "Stay neutral",
        "Ask 'what happened' not 'why did they do it'"
      ],
      warnings: [
        "Leading questions undermine evidence",
        "Don't share one person's account with another",
        "Respect if someone declines"
      ]
    },

    "Hold formal grievance hearing": {
      guidance: `**Hearing Structure:**
• Introductions
• Explain process
• Employee presents grievance
• Ask questions
• Consider evidence
• Adjourn to decide

**During Hearing:**
• Listen actively
• Take notes
• Stay impartial
• Allow employee to speak
• Consider companion's input
• Don't rush

**Atmosphere:**
• Professional but not intimidating
• Allow breaks if needed
• Be respectful
• Stay calm`,
      tips: [
        "Follow hearing script",
        "Allow emotional employee time",
        "Focus on resolving issue"
      ],
      warnings: [
        "Don't be defensive",
        "Avoid predetermined outcome",
        "Must genuinely listen"
      ]
    },

    "Allow employee to present case": {
      guidance: `**Employee's Opportunity:**
• Explain their grievance fully
• Provide evidence
• Call witnesses if relevant
• Respond to questions
• Explain impact on them

**Facilitating:**
• Give adequate time
• Don't interrupt
• Ask clarifying questions
• Consider evidence fairly
• Take concerns seriously

**Companion's Role:**
• Can address hearing
• Can confer with employee
• Can ask for adjournments
• Cannot answer for employee`,
      tips: [
        "Let employee tell their story",
        "Don't dismiss their feelings",
        "Focus on facts and solutions"
      ],
      warnings: [
        "Preventing employee from presenting is unfair",
        "Must consider their evidence",
        "Don't minimize their concerns"
      ]
    },

    "Adjourn to consider decision": {
      guidance: `**Adjournment:**
• Don't decide immediately
• Review all evidence
• Consider investigation findings
• Weigh different accounts
• Think about solutions

**Decision-Making:**
• Is grievance upheld (fully/partly)?
• What action is needed?
• Are there systemic issues?
• What can resolve this?
• Is mediation appropriate?

**Consultation:**
• Discuss with HR if needed
• Consider legal implications
• Think about precedent`,
      tips: [
        "Take adequate time",
        "Be objective",
        "Focus on resolution"
      ],
      warnings: [
        "Hasty decisions often wrong",
        "Must have clear reasoning",
        "Consider wider implications"
      ]
    },

    "Communicate decision in writing": {
      guidance: `**Letter Must Include:**
• Clear decision (upheld/not upheld)
• Findings of fact
• Reasons for decision
• Actions to be taken
• Timescales for actions
• Right of appeal

**Tone:**
• Professional and respectful
• Acknowledge employee's concerns
• Explain reasoning clearly
• Be constructive

**Timing:**
• Send within 5 working days
• Don't delay
• Keep employee informed if delay`,
      tips: [
        "Use outcome letter template",
        "Have HR review",
        "Send by email and post"
      ],
      warnings: [
        "Vague decision is unhelpful",
        "Must explain reasoning",
        "Delays damage trust"
      ]
    },

    "Explain reasons for decision": {
      guidance: `**Reasons Must:**
• Be clear and specific
• Reference evidence
• Explain how you reached decision
• Address key points raised
• Show you considered all factors

**If Upheld:**
• Explain what will change
• Set timescales
• Identify who's responsible
• Commit to monitoring

**If Not Upheld:**
• Explain why not proven
• Acknowledge employee's feelings
• Suggest alternatives if appropriate
• Explain appeal rights`,
      tips: [
        "Be thorough but clear",
        "Avoid jargon",
        "Show you took it seriously"
      ],
      warnings: [
        "Inadequate reasons cause resentment",
        "Don't be dismissive",
        "Must show fair consideration"
      ]
    },

    "Offer right of appeal": {
      guidance: `**Appeal Rights:**
• Employee can appeal decision
• Usually 5 working days
• Must explain how to appeal
• Appeal to more senior manager

**Grounds for Appeal:**
• Decision was wrong
• Process was unfair
• New evidence available
• Outcome inadequate

**Appeal Process:**
• Acknowledge appeal
• Arrange appeal hearing
• Genuinely reconsider
• Can uphold, vary, or overturn`,
      tips: [
        "Make appeal process clear",
        "Use different person for appeal",
        "Take appeals seriously"
      ],
      warnings: [
        "No genuine appeal is unfair",
        "Can't just rubber-stamp decision",
        "Must consider new evidence"
      ]
    },

    "Implement agreed changes": {
      guidance: `**Implementation:**
• Identify specific actions
• Assign responsibilities
• Set timescales
• Communicate to relevant people
• Allocate resources if needed

**Follow-Through:**
• Track progress
• Keep employee informed
• Address any obstacles
• Adjust if needed

**Documentation:**
• Record what was agreed
• Note implementation steps
• Document completion
• File for audit trail`,
      tips: [
        "Act promptly on agreed changes",
        "Don't promise what you can't deliver",
        "Keep employee updated"
      ],
      warnings: [
        "Failing to implement undermines process",
        "Delays damage trust",
        "Incomplete action breeds resentment"
      ]
    },

    "Monitor ongoing situation": {
      guidance: `**Monitoring:**
• Schedule follow-up meetings
• Check changes are working
• Ask for employee feedback
• Address any new concerns
• Adjust approach if needed

**Timescale:**
• Regular check-ins (monthly)
• Continue for reasonable period (3-6 months)
• Gradually reduce frequency
• Confirm when monitoring ends

**Documentation:**
• Keep notes of check-ins
• Record any issues
• Document improvements
• File for records`,
      tips: [
        "Be proactive, not reactive",
        "Show genuine interest in resolution",
        "Celebrate improvements"
      ],
      warnings: [
        "Don't assume issue is resolved",
        "Monitoring shouldn't feel like surveillance",
        "Be responsive to ongoing concerns"
      ]
    }
  },

  // PERFORMANCE-CAPABILITY
  "performance-capability": {
    "Set SMART objectives and expectations": {
      guidance: `**Foundation:** Clear objectives essential for fair process.

**SMART Criteria:**
• Specific: Exactly what to achieve
• Measurable: How you'll know it's done
• Achievable: Realistic given resources
• Relevant: Aligned to job role
• Time-bound: Clear deadline`,
      tips: [
        "Involve employee in setting",
        "Put in writing",
        "Review regularly"
      ],
      warnings: [
        "Vague objectives are unfair",
        "Don't move goalposts"
      ]
    },

    "Provide coaching/training and support plan": {
      guidance: `**Support Types:**
• One-on-one coaching sessions
• Training courses (internal/external)
• Mentoring from experienced colleague
• Job shadowing opportunities
• Additional supervision
• Resources or tools

**Support Plan:**
• Identify specific needs
• Schedule training/coaching
• Assign mentor if appropriate
• Set review dates
• Document plan

**Delivery:**
• Ensure support is actually provided
• Track attendance/completion
• Get feedback on usefulness
• Adjust if not working`,
      tips: [
        "Ask employee what support they need",
        "Be specific about what will be provided",
        "Follow through on commitments"
      ],
      warnings: [
        "Promising support but not providing undermines case",
        "Generic 'training will be provided' is insufficient",
        "Must give employee tools to succeed"
      ]
    },

    "Schedule follow-up review meeting": {
      guidance: `**Purpose:**
• Check progress against objectives
• Provide feedback
• Identify any obstacles
• Adjust support if needed
• Maintain momentum

**Scheduling:**
• Set date in advance
• Allow adequate time (1-2 weeks)
• Regular intervals (weekly/fortnightly)
• Confirm in writing
• Send calendar invite

**Preparation:**
• Review objectives
• Gather evidence of performance
• Prepare constructive feedback
• Consider what's working/not working`,
      tips: [
        "Don't cancel or postpone reviews",
        "Keep meetings constructive",
        "Document discussions"
      ],
      warnings: [
        "Irregular reviews suggest not taking seriously",
        "Don't ambush with criticism",
        "Must provide ongoing feedback, not save it up"
      ]
    },

    "Draft formal PIP with targets and timelines": {
      guidance: `**PIP Must Include:**
• Clear performance concerns with examples
• Specific, measurable targets (SMART)
• Support to be provided
• Review dates and frequency
• Timescale for improvement (4-12 weeks)
• Consequences if no improvement
• Right to representation

**Timescales:**
• Short-term issues: 4-6 weeks
• Complex/skill issues: 8-12 weeks
• Must be reasonable
• Consider training time needed

**Tone:**
• Supportive but clear about seriousness
• Focus on helping succeed
• Explain consequences honestly`,
      tips: [
        "Use PIP template",
        "Have employee sign receipt",
        "Schedule reviews in advance"
      ],
      warnings: [
        "Unrealistic timescales are unfair",
        "PIP should aim to improve, not just document failure",
        "Must provide promised support"
      ]
    },

    "Agree success measures and reasonable adjustments": {
      guidance: `**Success Measures:**
• How will improvement be measured?
• What does 'good enough' look like?
• Who will assess progress?
• How often will it be measured?
• What evidence will be used?

**Reasonable Adjustments:**
• Required if disability (Equality Act)
• Examples: flexible hours, equipment, modified duties
• Discuss with employee what would help
• Get Occupational Health advice
• Trial adjustments before ruling out

**Agreement:**
• Employee must understand and agree
• Put in writing
• Both parties sign
• Keep copy on file`,
      tips: [
        "Be specific about measurements",
        "Consider OH recommendations seriously",
        "Don't assume what adjustments are needed"
      ],
      warnings: [
        "Failing to consider adjustments can be discrimination",
        "Vague success measures are unfair",
        "Cost alone rarely makes adjustment unreasonable"
      ]
    },

    "Monitor weekly and document progress": {
      guidance: `**Monitoring:**
• Weekly check-in meetings
• Review progress against targets
• Provide ongoing feedback
• Identify any issues early
• Adjust support if needed

**Documentation:**
• Keep notes of each meeting
• Record achievements and concerns
• Note support provided
• File contemporaneously
• Be factual and objective

**Feedback:**
• Be specific and constructive
• Acknowledge improvements
• Address concerns promptly
• Focus on behaviors, not personality`,
      tips: [
        "Schedule meetings in advance",
        "Keep meetings brief but regular",
        "Celebrate small wins"
      ],
      warnings: [
        "Inconsistent monitoring undermines PIP",
        "Don't save up criticism",
        "Poor documentation weakens case"
      ]
    },

    "Mid-point PIP review and feedback": {
      guidance: `**Mid-Point Review:**
• Formal review halfway through PIP
• Assess progress so far
• Provide detailed feedback
• Decide if on track or adjustments needed
• Document discussion

**Assessment:**
• Compare performance to targets
• Review evidence gathered
• Consider if support is working
• Identify any obstacles
• Determine if timescale realistic

**Outcomes:**
• On track - continue as planned
• Some progress - extend support
• No progress - discuss next steps
• Exceeded - consider early completion`,
      tips: [
        "Be honest about progress",
        "Give employee clear feedback",
        "Consider extension if reasonable"
      ],
      warnings: [
        "Don't give false hope if no improvement",
        "Must genuinely assess progress",
        "Document mid-point review thoroughly"
      ]
    },

    "Send review hearing invitation (48h notice)": {
      guidance: `**Invitation Must Include:**
• Date, time, location
• Purpose: review PIP progress
• Evidence of performance during PIP
• Possible outcomes
• Right to be accompanied
• Opportunity to provide evidence

**Timing:**
• Minimum 48 hours notice
• Best practice: 5 working days
• After PIP period ends
• Allow time to prepare

**Evidence:**
• Performance data/metrics
• Feedback from managers
• Records of support provided
• Attendance at training
• Any incidents or achievements`,
      tips: [
        "Be objective - use data not opinions",
        "Acknowledge improvements even if insufficient",
        "Prepare for different outcomes"
      ],
      warnings: [
        "Don't prejudge outcome",
        "Ensure evidence is factual",
        "If dismissal possible, say so"
      ]
    },

    "Review evidence against PIP targets": {
      guidance: `**Review Process:**
• Compare actual performance to targets
• Assess each objective individually
• Consider quality and consistency
• Review all evidence fairly
• Look at overall picture

**Evidence Types:**
• Performance metrics/KPIs
• Manager observations
• Customer/colleague feedback
• Work samples or outputs
• Attendance records
• Training completion

**Assessment:**
• Has employee met targets?
• Has there been improvement?
• Is improvement sustainable?
• Were obstacles beyond their control?
• Did we provide promised support?`,
      tips: [
        "Be objective and fair",
        "Consider all evidence",
        "Don't cherry-pick negative examples"
      ],
      warnings: [
        "Must base decision on evidence, not feelings",
        "Consider if targets were realistic",
        "Account for any mitigating factors"
      ]
    },

    "Consider extension or progression to sanction": {
      guidance: `**Decision Options:**
• **Success:** PIP completed, return to normal
• **Partial success:** Extend PIP with adjusted targets
• **No improvement:** Progress to formal sanction
• **Some improvement:** Extend with continued support

**Factors to Consider:**
• Degree of improvement shown
• Reasons for any shortfall
• Employee's effort and engagement
• Whether more time would help
• Impact on business
• Consistency with previous cases

**Extension:**
• Only if reasonable prospect of success
• Set new timescale (typically 4-6 weeks)
• Adjust targets if needed
• Provide additional support`,
      tips: [
        "Be fair but realistic",
        "Don't extend indefinitely",
        "Consider if role is right fit"
      ],
      warnings: [
        "Multiple extensions can be unfair to employee",
        "Must have genuine belief extension will help",
        "Don't extend just to delay difficult decision"
      ]
    },

    "Confirm outcome in writing": {
      guidance: `**Outcome Letter Must Include:**
• Clear decision
• Assessment of performance during PIP
• Evidence relied upon
• Reasons for decision
• Next steps (if successful)
• Sanction (if unsuccessful)
• Right of appeal

**If Successful:**
• Congratulate employee
• Confirm return to normal working
• Set ongoing expectations
• Note any continued support

**If Unsuccessful:**
• Explain why targets not met
• Detail next steps (warning/dismissal process)
• Explain right of appeal
• Set timescales`,
      tips: [
        "Send within 5 working days",
        "Be clear and specific",
        "Have HR review letter"
      ],
      warnings: [
        "Vague outcome is unhelpful",
        "Must explain reasoning",
        "Letter may be used in tribunal"
      ]
    },

    "Set further monitoring or escalate to dismissal process": {
      guidance: `**Further Monitoring:**
• If successful, set ongoing review period
• Regular check-ins (monthly for 3-6 months)
• Confirm expectations
• Provide continued support
• Gradually reduce frequency

**Escalation to Dismissal:**
• If PIP unsuccessful and dismissal considered
• Follow formal dismissal process
• Ensure fair procedure
• Get legal advice
• Consider alternatives (redeployment, demotion)

**Documentation:**
• Record decision and reasoning
• Note any ongoing support
• File all PIP documentation
• Keep for tribunal defense if needed`,
      tips: [
        "Be clear about next steps",
        "Don't leave employee in limbo",
        "Follow proper process if escalating"
      ],
      warnings: [
        "Can't dismiss without following fair process",
        "Must consider alternatives to dismissal",
        "Ensure consistency with previous cases"
      ]
    },

    "Explain right of appeal": {
      guidance: `**Appeal Rights:**
• Employee can appeal decision
• Usually 5 working days to submit
• Must explain how to appeal
• Appeal to more senior manager

**Grounds for Appeal:**
• Decision was wrong
• Process was unfair
• Targets were unrealistic
• Support not provided as promised
• New evidence available

**Appeal Process:**
• Acknowledge appeal promptly
• Arrange appeal hearing
• Genuinely reconsider decision
• Can uphold, vary, or overturn
• Communicate outcome in writing`,
      tips: [
        "Make appeal process clear",
        "Use different person for appeal",
        "Take appeals seriously"
      ],
      warnings: [
        "No genuine appeal is unfair",
        "Can't just rubber-stamp decision",
        "Must consider new evidence fairly"
      ]
    }
  },

  // ABSENCE-MANAGEMENT
  "absence-management": {
    "Consider reasonable adjustments/phased return": {
      guidance: `**Legal Duty:** Equality Act requires reasonable adjustments.

**Phased Return:**
• Gradual increase in hours over 2-6 weeks
• Typical: 50% → 75% → 100%
• Must be supported by medical advice`,
      tips: [
        "Ask employee what they need",
        "Consult Occupational Health",
        "Trial before ruling out"
      ],
      warnings: [
        "Failing to consider can be discrimination",
        "Cost alone rarely makes unreasonable"
      ]
    },

    "Obtain fit notes/medical certificates": {
      guidance: `**Purpose:**
• Evidence of illness/incapacity
• Understand prognosis
• Identify any adjustments needed
• Support absence management decisions

**Fit Notes:**
• Required after 7 days absence
• May state 'not fit' or 'may be fit with adjustments'
• Note expiry date
• Request renewal if absence continues

**Management:**
• Keep copies on file
• Respect medical confidentiality
• Don't pressure employee to return before fit
• Follow GP recommendations where possible`,
      tips: [
        "Request fit notes promptly",
        "Check expiry dates",
        "Store securely (confidential medical data)"
      ],
      warnings: [
        "Can't force return without medical clearance",
        "Respect doctor's recommendations",
        "GDPR applies to medical information"
      ]
    },

    "Maintain regular contact with employee": {
      guidance: `**Purpose:**
• Show you care about employee's wellbeing
• Keep informed of progress
• Discuss return to work plans
• Maintain connection to workplace

**Frequency:**
• Weekly or fortnightly (depending on absence length)
• More frequent for short-term, less for long-term
• Agree contact method with employee
• Respect if employee requests less contact

**Content:**
• Ask how they're doing
• Update on work matters (if appropriate)
• Discuss return to work plans
• Offer support
• Don't pressure to return

**Method:**
• Phone call (preferred)
• Email or text if employee prefers
• Home visit only if agreed
• Respect employee's privacy`,
      tips: [
        "Be genuine and supportive",
        "Don't discuss work problems",
        "Keep records of contact"
      ],
      warnings: [
        "Too frequent contact can be harassment",
        "Don't pressure employee about return date",
        "Respect if employee doesn't want contact"
      ]
    },

    "Refer to Occupational Health": {
      guidance: `**When to Refer:**
• Long-term absence (4+ weeks)
• Frequent short-term absences
• Disability-related absence
• Stress/mental health issues
• Need for adjustments assessment
• Before considering dismissal

**Referral Questions:**
• What is the medical condition?
• What is the prognosis?
• When might employee return?
• What adjustments would help?
• Is condition a disability under Equality Act?
• Can employee perform role with adjustments?

**Process:**
• Get employee's consent
• Provide OH with job description
• Ask specific questions
• Share OH report with employee
• Discuss recommendations`,
      tips: [
        "Refer early, not as last resort",
        "Ask specific questions",
        "Follow OH recommendations"
      ],
      warnings: [
        "Need employee consent for referral",
        "OH advice is guidance, not instruction",
        "Ignoring OH recommendations is risky"
      ]
    },

    "Invite to case management meeting": {
      guidance: `**Purpose:**
• Supportive discussion about absence
• Understand barriers to return
• Explore adjustments needed
• Agree return to work plan
• NOT disciplinary

**Invitation Must Include:**
• Date, time, location (or video call option)
• Purpose (supportive, not disciplinary)
• Who will attend
• Right to be accompanied
• Opportunity to provide medical evidence
• Can reschedule if health prevents attendance

**Tone:**
• Supportive and empathetic
• Focus on "how can we help?"
• Not "why aren't you back yet?"`,
      tips: [
        "Offer flexibility with format",
        "Don't pressure to return before ready",
        "Focus on solutions"
      ],
      warnings: [
        "Treating as disciplinary can be discrimination",
        "Don't make assumptions about health",
        "Respect medical confidentiality"
      ]
    },

    "Review OH report and recommendations": {
      guidance: `**Review Process:**
• Read report thoroughly
• Understand medical condition
• Note prognosis and timescales
• Identify recommended adjustments
• Consider if disability under Equality Act
• Assess feasibility of recommendations

**Key Questions:**
• Can employee return to work?
• When might they return?
• What adjustments are recommended?
• Are adjustments reasonable?
• Can role be performed with adjustments?
• Is redeployment recommended?

**Action:**
• Discuss report with employee
• Implement recommended adjustments
• Seek clarification if needed
• Document decisions`,
      tips: [
        "Take OH recommendations seriously",
        "Discuss with employee",
        "Trial adjustments before ruling out"
      ],
      warnings: [
        "Ignoring OH advice is risky",
        "Must consider recommended adjustments",
        "Document reasons if not implementing"
      ]
    },

    "Agree return-to-work plan and timescales": {
      guidance: `**Return Plan Should Include:**
• Target return date
• Phased return schedule (if appropriate)
• Adjustments to be made
• Support to be provided
• Review dates
• Contact person for concerns

**Phased Return Example:**
• Week 1-2: 50% hours
• Week 3-4: 75% hours
• Week 5+: 100% hours
• Adjust based on progress

**Agreement:**
• Discuss with employee
• Get their input
• Put in writing
• Both parties sign
• Keep under review`,
      tips: [
        "Be flexible and supportive",
        "Build in review points",
        "Allow for adjustments"
      ],
      warnings: [
        "Don't rush return",
        "Must be supported by medical advice",
        "Be prepared to adjust plan"
      ]
    },

    "Send hearing invitation with evidence": {
      guidance: `**When Required:**
• If considering dismissal for capability
• After exhausting other options
• With medical evidence
• Following OH advice

**Invitation Must Include:**
• Date, time, location
• Purpose: capability hearing
• Evidence (OH reports, absence records)
• Possible outcomes (including dismissal)
• Right to be accompanied
• Opportunity to provide evidence

**Evidence:**
• Absence records
• OH reports
• Fit notes
• Records of adjustments tried
• Contact notes
• Impact on business`,
      tips: [
        "Give adequate notice (5-7 days)",
        "Provide all evidence in advance",
        "Be clear dismissal is possible"
      ],
      warnings: [
        "Must have medical evidence",
        "Must show you considered adjustments",
        "Ensure process is fair"
      ]
    },

    "Conduct capability/attendance hearing": {
      guidance: `**Hearing Structure:**
• Explain purpose and process
• Present evidence (absence records, OH reports)
• Employee responds and presents case
• Discuss adjustments tried
• Consider alternatives
• Adjourn to decide

**Considerations:**
• Has employee had reasonable time to recover?
• Have all adjustments been tried?
• Is further absence likely?
• Can role be performed?
• Is redeployment possible?
• Is dismissal reasonable response?

**Tone:**
• Professional but compassionate
• Acknowledge employee's situation
• Focus on business needs vs. employee capability`,
      tips: [
        "Be empathetic but clear",
        "Consider all evidence fairly",
        "Don't rush decision"
      ],
      warnings: [
        "Dismissing without considering adjustments is discrimination",
        "Must show you've explored alternatives",
        "Ensure decision is reasonable"
      ]
    },

    "Consider alternatives (adjustments/redeployment)": {
      guidance: `**Alternatives to Dismissal:**
• Further adjustments to current role
• Reduced hours permanently
• Different duties within role
• Redeployment to different role
• Ill-health retirement (if applicable)
• Extended unpaid leave

**Redeployment:**
• Check for suitable vacancies
• Consider with adjustments
• Offer trial period
• May need to create role
• Consider lower grade if appropriate

**Assessment:**
• Is alternative role available?
• Can employee perform it?
• With or without adjustments?
• Is it reasonable to offer?
• What are costs/impacts?`,
      tips: [
        "Genuinely explore alternatives",
        "Consult with employee",
        "Document consideration"
      ],
      warnings: [
        "Must consider redeployment before dismissal",
        "Can't dismiss if reasonable alternative exists",
        "Failure to consider is unfair"
      ]
    },

    "Confirm outcome in writing": {
      guidance: `**Outcome Letter Must Include:**
• Clear decision
• Reasons for decision
• Evidence relied upon
• Alternatives considered
• Effective date (if dismissal)
• Notice period or payment
• Right of appeal

**If Returning:**
• Confirm return date
• Detail adjustments
• Set review dates
• Confirm support

**If Dismissal:**
• Explain why dismissal necessary
• Show alternatives considered
• Detail notice and final pay
• Explain appeal rights`,
      tips: [
        "Send within 5 working days",
        "Be clear and compassionate",
        "Have legal review if dismissal"
      ],
      warnings: [
        "Inadequate reasons make dismissal unfair",
        "Must show you considered alternatives",
        "Letter may be used in tribunal"
      ]
    },

    "Explain right of appeal": {
      guidance: `**Appeal Rights:**
• Employee can appeal decision
• Usually 5 working days
• Must explain how to appeal
• Appeal to more senior manager

**Grounds:**
• Decision was wrong
• Process was unfair
• Alternatives not properly considered
• Medical evidence not considered
• New evidence available

**Appeal Process:**
• Acknowledge promptly
• Arrange appeal hearing
• Genuinely reconsider
• Can uphold, vary, or overturn
• Communicate outcome`,
      tips: [
        "Make process clear",
        "Use different person",
        "Take seriously"
      ],
      warnings: [
        "No genuine appeal is unfair",
        "Must reconsider properly",
        "Consider new evidence"
      ]
    },

    "Monitor attendance and review plan": {
      guidance: `**Monitoring:**
• Regular check-ins (weekly/monthly)
• Review how adjustments working
• Track attendance
• Address any concerns
• Adjust plan if needed

**Review Points:**
• End of phased return
• 3 months after return
• 6 months after return
• When adjustments expire (if temporary)

**Documentation:**
• Keep attendance records
• Note any issues
• Record adjustments made
• Document reviews`,
      tips: [
        "Be supportive, not punitive",
        "Celebrate successful return",
        "Be flexible with adjustments"
      ],
      warnings: [
        "Don't assume issue is resolved",
        "Monitoring shouldn't feel like surveillance",
        "Be responsive to ongoing needs"
      ]
    }
  },

  // EXIT-DISMISSAL
  "exit-dismissal": {
    "Send hearing invitation (>=48h notice) with rights": {
      guidance: `**Critical:** Dismissal hearing must follow fair process.

**Must Include:**
• Clear statement dismissal is possible
• All allegations and evidence
• Right to be accompanied
• Right to appeal

**Timing:** Minimum 48 hours, best practice 7-10 days`,
      tips: [
        "Send by recorded delivery",
        "Ensure decision-maker independent"
      ],
      warnings: [
        "Inadequate notice makes dismissal unfair",
        "Must warn dismissal possible"
      ]
    },

    "Appoint investigating officer": {
      guidance: `**Purpose:**
• Gather facts before dismissal decision
• Ensure process is fair
• Collect evidence systematically
• Prepare report for decision-maker

**Independence:**
• Not involved in the issue
• No predetermined views
• Sufficient seniority
• Trained in investigations

**Brief:**
• Terms of reference
• Timeline expectations
• Confidentiality requirements
• Support available`,
      tips: [
        "Choose experienced investigator",
        "Provide investigation checklist",
        "Set realistic timescales"
      ],
      warnings: [
        "Biased investigator undermines process",
        "Investigator shouldn't be decision-maker",
        "Poor investigation makes dismissal unfair"
      ]
    },

    "Gather evidence and witness statements": {
      guidance: `**Evidence Types:**
• Witness statements (written, signed)
• Documents and emails
• CCTV footage
• Records (attendance, performance, etc.)
• Previous warnings
• Relevant policies

**Witness Statements:**
• Interview witnesses separately
• Take detailed notes
• Ask open questions
• Get statement signed
• Keep confidential

**Preservation:**
• Act quickly (CCTV may be overwritten)
• Maintain chain of custody
• Store securely
• Number and date all evidence`,
      tips: [
        "Get written statements, not verbal",
        "Keep evidence log",
        "Redact third-party personal data"
      ],
      warnings: [
        "Missing evidence weakens case",
        "Tampering with evidence is serious",
        "Respect data protection laws"
      ]
    },

    "Hold hearing with independent chair": {
      guidance: `**Chair Requirements:**
• Independent (not investigator)
• Sufficient seniority to dismiss
• Trained in dismissal procedures
• Impartial and open-minded

**Hearing Structure:**
• Introductions and explain process
• Present evidence and findings
• Employee responds and presents case
• Questions and clarifications
• Adjourn to consider decision

**Considerations:**
• Is misconduct/capability proven?
• Is dismissal appropriate sanction?
• Have alternatives been considered?
• Is process fair?
• Is decision reasonable?`,
      tips: [
        "Follow hearing script",
        "Take detailed notes",
        "Don't rush to judgment"
      ],
      warnings: [
        "Predetermined decision is unfair",
        "Chair must be genuinely independent",
        "Procedural errors make dismissal unfair"
      ]
    },

    "Confirm decision and issue notice": {
      guidance: `**Dismissal Letter Must Include:**
• Clear statement of dismissal
• Effective date of termination
• Full reasons for dismissal
• Evidence relied upon
• Notice period or payment in lieu
• Final pay details
• Right to appeal (deadline and process)
• Return of company property

**Reasons:**
• Must be clear and detailed
• Explain why dismissal appropriate
• Reference evidence
• Show alternatives considered
• Explain why lesser sanction not appropriate

**Notice:**
• Statutory minimum (1 week per year, max 12)
• Contractual notice if longer
• Payment in lieu if appropriate
• Garden leave if appropriate`,
      tips: [
        "Send within 5 working days of hearing",
        "Have legal review letter",
        "Send by recorded delivery and email"
      ],
      warnings: [
        "Inadequate reasons make dismissal unfair",
        "Wrong notice period = breach of contract",
        "Must offer genuine appeal",
        "Letter will be used in tribunal"
      ]
    },

    "Calculate final pay, holiday, deductions": {
      guidance: `**Final Pay Components:**
• Salary up to termination date
• Payment in lieu of notice (if applicable)
• Accrued holiday pay
• Outstanding expenses
• Bonus (if contractually due)
• Pension contributions

**Deductions:**
• Overpaid salary
• Outstanding loans
• Company property not returned (if contract allows)
• Tax and National Insurance

**Holiday Pay:**
• Calculate accrued but untaken holiday
• Pro-rata for part year
• Must pay for accrued holiday
• Can deduct if over-taken (if contract allows)

**Timing:**
• Pay on normal pay date or earlier
• Provide itemized payslip
• Explain all deductions`,
      tips: [
        "Use final pay calculator",
        "Check contract for notice period",
        "Get payroll to verify calculations"
      ],
      warnings: [
        "Errors cause additional claims",
        "Must pay accrued holiday",
        "Can only deduct if contract allows",
        "Provide clear breakdown"
      ]
    },

    "Revoke systems access and recover property": {
      guidance: `**Systems Access:**
• Disable email and network access
• Remove from systems (HR, payroll, etc.)
• Cancel security passes
• Retrieve company mobile/laptop
• Change passwords if necessary

**Company Property:**
• List all items to be returned
• Arrange collection/return
• Check condition
• Get signed receipt
• Deduct for non-return only if contract allows

**Timing:**
• Usually on termination date
• Immediate if gross misconduct
• During notice period if garden leave

**Data:**
• Ensure employee data deleted from devices
• Check for confidential information
• Secure any work in progress`,
      tips: [
        "Use offboarding checklist",
        "Coordinate with IT",
        "Be respectful in process"
      ],
      warnings: [
        "Don't disable access before informing employee",
        "Respect employee's personal data",
        "Can't withhold final pay for unreturned property"
      ]
    },

    "Conduct exit interview and document learnings": {
      guidance: `**Exit Interview Purpose:**
• Understand reasons for exit
• Gather feedback on organization
• Identify improvement areas
• Maintain positive relationship
• Reduce legal risk

**Questions to Ask:**
• What led to this situation?
• What could have been done differently?
• Any concerns about how handled?
• Feedback on management/organization?
• Plans for future?

**Tone:**
• Professional and respectful
• Non-confrontational
• Listen without being defensive
• Thank for their service

**Documentation:**
• Record key points
• Identify themes/patterns
• Share learnings with management
• Implement improvements
• Keep confidential`,
      tips: [
        "Offer exit interview (don't force)",
        "Use neutral person if possible",
        "Focus on learning, not justifying"
      ],
      warnings: [
        "Don't use to gather evidence against employee",
        "Respect if employee declines",
        "Keep feedback confidential",
        "Act on feedback where appropriate"
      ]
    }
  },

  // TRIBUNAL-PREP
  "tribunal-prep": {
    "Review ET1, merits, and risk assessment": {
      guidance: `**Critical First Step:** Proper assessment determines strategy.

**Review:**
• What claims are made?
• What remedy sought?
• Key allegations and timeline?

**Risk Assessment:**
• Financial risk
• Reputational risk
• Settlement value`,
      tips: [
        "Get legal advice early",
        "Be honest about weaknesses",
        "Calculate worst-case"
      ],
      warnings: [
        "Underestimating risk costly",
        "Weak cases should settle"
      ]
    },

    "Appoint solicitor/representative": {
      guidance: `**When to Appoint:**
• As soon as ET1 received
• Specialist employment solicitor
• Experience in tribunal representation
• Good track record

**Selection Criteria:**
• Employment law specialist
• Tribunal experience
• Local to tribunal (if possible)
• Reasonable fees
• Good communication
• Available for hearing dates

**Brief:**
• Provide all case documents
• Explain background and context
• Share concerns and objectives
• Discuss budget and costs
• Agree strategy

**Costs:**
• Typical: £10k-£50k+ depending on complexity
• Get estimate upfront
• Understand billing structure
• Consider legal expenses insurance`,
      tips: [
        "Get recommendations from other employers",
        "Interview 2-3 solicitors before choosing",
        "Agree fee structure in writing"
      ],
      warnings: [
        "Don't try to handle complex cases alone",
        "Cheap solicitor may cost more in long run",
        "Ensure solicitor has tribunal experience"
      ]
    },

    "Prepare and file ET3 response": {
      guidance: `**Critical Deadline:** 28 days from receiving ET1 - missing this is catastrophic.

**ET3 Must Include:**
• Whether you resist claim (yes/no for each)
• Full grounds of resistance
• Your version of events (detailed)
• Legal basis for defense
• Procedural objections (if any)
• Counterclaim (if applicable)

**Drafting:**
• Be detailed but don't volunteer weaknesses
• Stick to facts you can prove
• Address each allegation specifically
• Identify agreed/disputed facts
• Explain decision-making process
• Show fair procedures followed

**Timeline:**
• Day 1-7: Gather documents, take instructions
• Day 8-14: Draft ET3
• Day 15-21: Review and finalize
• Day 22-28: File (don't leave to last day!)`,
      tips: [
        "Use solicitor to draft ET3",
        "Attach key documents if helpful",
        "Confirm receipt from tribunal"
      ],
      warnings: [
        "Missing 28-day deadline = default judgment",
        "Extensions rarely granted",
        "Weak ET3 undermines case throughout",
        "Inconsistencies damage credibility"
      ]
    },

    "Disclosure, witness list, and bundle plan": {
      guidance: `**Disclosure:**
• Provide all relevant documents
• Even if unhelpful to your case
• Includes emails, notes, policies, records
• Duty is ongoing throughout case
• Organize chronologically

**Witness List:**
• Identify who will give evidence
• Decision-makers in process
• Investigators
• Line managers
• HR advisors
• Limit to essential witnesses (3-5 typically)

**Bundle Plan:**
• Agreed bundle of documents
• Paginated and indexed
• Chronological order
• Include both parties' documents
• Usually 200-500 pages
• Provide to tribunal and parties in advance`,
      tips: [
        "Start disclosure early",
        "Use document management system",
        "Discuss bundle with other side"
      ],
      warnings: [
        "Failing to disclose can be fatal",
        "Don't destroy documents once claim filed",
        "Tribunal can draw adverse inferences"
      ]
    },

    "Draft witness statements and collate bundle": {
      guidance: `**Witness Statements:**
• Written evidence of each witness
• First person, chronological narrative
• Cover relevant events and decisions
• Explain reasoning for decisions
• Attach documents referred to
• Sign statement of truth

**Drafting Process:**
• Interview witness thoroughly
• Draft statement in their words
• Witness reviews and corrects
• Finalize and sign
• Exchange with other side (usually 7-14 days before hearing)

**Bundle:**
• Compile all documents
• Number pages consecutively
• Create index
• Organize chronologically or by topic
• Include both parties' documents
• Provide to tribunal (usually 7 days before)

**Quality:**
• Statements must be truthful
• Consistent with documents
• Address key issues
• Not argumentative`,
      tips: [
        "Start witness statements early",
        "Multiple drafts normal",
        "Check consistency between witnesses"
      ],
      warnings: [
        "Inconsistent statements damage credibility",
        "Don't coach witnesses",
        "Late statements may be excluded"
      ]
    },

    "Brief counsel and rehearse witnesses": {
      guidance: `**Briefing Counsel:**
• If using barrister for hearing
• Provide full bundle and statements
• Explain background and strategy
• Discuss strengths and weaknesses
• Agree approach for hearing
• Discuss settlement prospects

**Witness Preparation:**
• Explain tribunal process
• Review their statement
• Practice being cross-examined
• Discuss likely questions
• Explain how to answer
• Manage expectations

**Rehearsal:**
• Mock cross-examination
• Practice staying calm
• Stick to facts
• Don't speculate
• Say "I don't know" if unsure
• Don't argue with questioner

**Final Prep:**
• Logistics (location, timing, dress code)
• Who will attend
• Order of witnesses
• Breaks and support`,
      tips: [
        "Prepare witnesses thoroughly",
        "Practice difficult questions",
        "Keep witnesses calm and confident"
      ],
      warnings: [
        "Don't coach witnesses to lie",
        "Over-preparation can make witnesses robotic",
        "Nervous witnesses need extra support"
      ]
    },

    "Attend hearing and manage evidence": {
      guidance: `**Hearing Day:**
• Arrive early
• Check room setup
• Introduce team to tribunal
• Have documents organized
• Take notes throughout

**Presenting Evidence:**
• Opening statement (if allowed)
• Call witnesses in order
• Examination in chief (if allowed)
• Cross-examination by other side
• Re-examination (if needed)
• Closing submissions

**During Hearing:**
• Stay professional and calm
• Don't interrupt
• Take detailed notes
• Pass notes to representative
• Be prepared to adjust strategy
• Consider settlement discussions

**After Each Day:**
• Debrief with team
• Review what went well/badly
• Prepare for next day
• Brief witnesses for next day`,
      tips: [
        "Dress professionally",
        "Be respectful to tribunal",
        "Stay calm even if things go badly"
      ],
      warnings: [
        "Don't show emotion in tribunal",
        "Don't discuss case in public areas",
        "Respect tribunal's authority"
      ]
    },

    "Record judgment and consider appeal": {
      guidance: `**Judgment:**
• Tribunal may give oral judgment
• Written judgment follows (usually 2-4 weeks)
• Read carefully and understand reasoning
• Calculate financial implications
• Consider impact on business

**If You Win:**
• Celebrate but be gracious
• Consider claimant may appeal
• Implement any recommendations
• Learn lessons for future

**If You Lose:**
• Don't panic
• Analyze reasons for loss
• Calculate compensation
• Consider whether to appeal
• Implement changes to prevent recurrence

**Appeal:**
• Only on point of law (not facts)
• To Employment Appeal Tribunal (EAT)
• 42 days from written judgment
• Need legal advice
• Consider prospects and costs
• May be better to accept and move on`,
      tips: [
        "Get legal advice on appeal prospects",
        "Consider commercial factors",
        "Learn from the case"
      ],
      warnings: [
        "Appeals rarely succeed",
        "Costs can escalate significantly",
        "May make matters worse",
        "Consider settlement even after losing"
      ]
    }
  },

  // SETTLEMENT
  settlement: {
    "Initiate ACAS Early Conciliation": {
      guidance: `**ACAS Service:** Free, confidential conciliation.

**Benefits:**
• Cheaper than tribunal
• Faster resolution
• Confidential
• Avoids uncertainty`,
      tips: [
        "Respond quickly to ACAS",
        "Be realistic about value",
        "Get authority to settle"
      ],
      warnings: [
        "Discussions without prejudice",
        "Must be recorded in COT3"
      ]
    },

    "Assess merits, costs, and strategy": {
      guidance: `**Merits Assessment:**
• What are our chances of winning? (%)
• What are our weaknesses?
• What evidence do we have?
• What evidence does employee have?
• How strong are their claims?

**Cost Analysis:**
• Legal fees to defend: £10k-£50k+
• Management time cost
• Potential compensation if we lose
• Basic award + compensatory award
• Possible 25% ACAS uplift
• Reputational damage

**Strategy Options:**
• Defend fully (if strong case)
• Settle early (if weak case)
• Make Part 36 offer
• Admit liability, contest remedy
• Use ACAS conciliation

**Commercial Decision:**
• Cost of defending vs settlement value
• Likelihood of success
• Reputational risk
• Management distraction
• Precedent for other cases`,
      tips: [
        "Be brutally honest about weaknesses",
        "Calculate worst-case scenario",
        "Consider non-financial factors"
      ],
      warnings: [
        "Over-optimism is expensive",
        "Defending weak cases wastes money",
        "Consider commercial reality, not just legal position"
      ]
    },

    "Obtain internal approval and budget": {
      guidance: `**Approval Process:**
• Identify who can authorize settlement
• Prepare business case
• Explain risks and costs
• Recommend settlement range
• Get written authority

**Business Case Should Include:**
• Summary of claims
• Merits assessment
• Cost to defend vs settle
• Recommended settlement range
• Reputational considerations
• Timeline and process

**Budget:**
• Settlement payment
• Legal fees
• Employee's legal contribution (£500-£1,500)
• Tax implications
• Total cost

**Authority Levels:**
• Who can approve what amounts?
• Board approval needed?
• Insurance involvement?
• Get sign-off before negotiating`,
      tips: [
        "Present clear recommendation",
        "Include worst-case scenario",
        "Get authority in writing"
      ],
      warnings: [
        "Don't negotiate without authority",
        "Ensure budget includes all costs",
        "Consider tax treatment in budget"
      ]
    },

    "Draft without prejudice offer and negotiate terms": {
      guidance: `**Without Prejudice:**
• Marked 'Without Prejudice'
• Cannot be shown to tribunal
• Allows frank negotiation
• Protects both parties

**Offer Should Include:**
• Compensation amount
• Tax treatment breakdown
• Notice pay (if applicable)
• Holiday pay
• Legal fees contribution
• Reference terms
• Confidentiality clause
• Non-disparagement clause
• Timescale for acceptance

**Negotiation:**
• Start below target settlement
• Leave room to negotiate
• Explain rationale for offer
• Be prepared to compromise
• Set deadline for response
• Consider non-financial terms

**Tax Treatment:**
• First £30k compensation tax-free
• Notice pay is taxable
• Holiday pay is taxable
• Structure to minimize tax`,
      tips: [
        "Be realistic but leave negotiating room",
        "Consider what matters to employee",
        "Use 'without prejudice' on all offers"
      ],
      warnings: [
        "Don't make offer you can't honor",
        "Ensure tax treatment is correct",
        "Get legal advice on offer terms"
      ]
    },

    "Draft settlement agreement (payments, tax, references)": {
      guidance: `**Settlement Agreement Must Include:**
• Payment amount and breakdown
• Tax treatment of each element
• Payment date and method
• Waiver of all claims
• Confidentiality clause
• Non-disparagement clause
• Reference terms (agreed wording)
• Return of property
• Contribution to legal fees
• Employee must get independent legal advice

**Payment Breakdown:**
• Compensation: £X (tax-free up to £30k)
• Notice pay: £X (taxable)
• Holiday pay: £X (taxable)
• Legal fees: £X
• Total: £X

**Reference:**
• Agree exact wording
• Attach as schedule
• Confirm who will provide
• Set process for requests

**Legal Requirements:**
• Must be in writing
• Relate to specific complaint
• Employee must have independent legal advice
• Adviser must have indemnity insurance
• Certificate from adviser required`,
      tips: [
        "Use settlement agreement template",
        "Have solicitor draft/review",
        "Be clear on tax treatment"
      ],
      warnings: [
        "Agreement not binding without legal advice certificate",
        "Ensure waiver covers all potential claims",
        "Get tax advice if large payment"
      ]
    },

    "Employee obtains independent legal advice": {
      guidance: `**Legal Requirement:**
• Employee must get independent legal advice
• Adviser must be qualified solicitor or authorized person
• Must have professional indemnity insurance
• Adviser signs certificate confirming advice given

**Employer's Role:**
• Contribute to legal fees (typically £500-£1,500)
• Allow reasonable time (5-10 working days)
• Don't pressure employee
• Provide draft agreement in advance

**Adviser's Role:**
• Explain terms of agreement
• Advise on whether fair
• Explain what rights being waived
• Confirm employee understands
• Sign certificate

**If Employee Can't Get Advice:**
• Extend deadline if reasonable
• Consider increasing contribution
• Don't proceed without certificate
• Agreement won't be binding`,
      tips: [
        "Standard contribution is £500-£1,500",
        "Allow adequate time",
        "Don't pressure employee to sign quickly"
      ],
      warnings: [
        "Agreement invalid without legal advice certificate",
        "Can't rush this step",
        "Employee may decide not to sign after advice"
      ]
    },

    "Execute agreement and process payments": {
      guidance: `**Execution:**
• Both parties sign agreement
• Obtain legal advice certificate
• Check all terms completed
• File signed copy securely

**Payment Processing:**
• Process through payroll
• Apply correct tax treatment
• Pay by agreed deadline
• Provide itemized payslip
• Confirm receipt

**Tax Treatment:**
• First £30k compensation: tax-free
• Notice pay: PAYE and NI
• Holiday pay: PAYE and NI
• Legal fees: paid gross
• Get payroll to verify

**Confirmation:**
• Confirm payment made
• Provide payslip
• Confirm reference arrangements
• Arrange property return
• Close case file`,
      tips: [
        "Pay promptly on agreed date",
        "Ensure tax treatment correct",
        "Keep copy of all documents"
      ],
      warnings: [
        "Late payment can breach agreement",
        "Wrong tax treatment causes problems",
        "Ensure payroll understands settlement payments"
      ]
    },

    "Withdraw proceedings and implement terms": {
      guidance: `**Withdraw Proceedings:**
• If tribunal claim filed, must withdraw
• File withdrawal notice with tribunal
• Confirm with ACAS (if COT3)
• Get confirmation from tribunal
• Keep copy for records

**Implement Terms:**
• Process payments as agreed
• Provide agreed reference
• Return any property
• Update HR systems
• Close employee file
• Implement any agreed changes

**Reference:**
• Provide as agreed
• Use exact wording
• Confirm who will provide
• Brief relevant people
• Keep copy on file

**Confidentiality:**
• Brief relevant people on confidentiality
• Don't discuss settlement
• Respect employee's privacy
• Monitor compliance

**Lessons Learned:**
• What went wrong?
• What could be improved?
• Policy/procedure changes needed?
• Training requirements?
• Share learnings (confidentially)`,
      tips: [
        "Implement all terms promptly",
        "Keep detailed records",
        "Learn from the case"
      ],
      warnings: [
        "Breaching settlement terms can restart claim",
        "Ensure confidentiality maintained",
        "Don't badmouth employee after settlement"
      ]
    }
  }
};

/**
 * Get case-level guidance for Overview tab
 */
export const getCaseTypeGuidance = (caseType) => {
  return caseTypeGuidance[caseType] || null;
};

/**
 * Get guidance for a specific action
 */
export const getActionGuidance = (caseType, actionName) => {
  const caseGuidance = actionGuidance[caseType];
  if (!caseGuidance) return null;
  
  return caseGuidance[actionName] || null;
};
